<template>
	<transition name="modal-fade">
		<div
			v-if="isVisible"
			class="modal-mask"
			@click="onMaskClick"
			role="dialog"
			aria-modal="true"
			:aria-label="title"
		>
			<div class="modal-wrap" :style="{ width }" @click.stop>
				<div class="modal-header">
					<!-- 弹窗头部标题 -->
					<slot name="title">
						<div class="modal-title">{{ title }}</div>
					</slot>
					<button v-if="closable" class="modal-close" type="button" aria-label="close" @click="handleCancel">
						x
					</button>
				</div>

				<div v-if="!destroyOnClose || isVisible" class="modal-body">
					<!-- 弹窗主体内容 -->
					<slot></slot>
				</div>

				<div v-if="showFooter" class="modal-footer">
					<!-- 调用方可通过 footer 插槽自定义底部按钮；未提供时使用默认按钮。 -->
					<slot name="footer">
						<button class="modal-btn" type="button" :disabled="confirmLoading" @click="handleCancel">
							{{ cancelText }}
						</button>
						<button class="modal-btn modal-btn-primary" type="button" :disabled="confirmLoading" @click="handleOk">
							<span v-if="confirmLoading">...</span>
							<span v-else>{{ okText }}</span>
						</button>
					</slot>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
export default {
	name: "CommonModal",
	model: {
		prop: "value",
		event: "input",
	},
	props: {
		value: {
			type: Boolean,
			default: false,
		},
		// 弹窗是否可见
		visible: {
			type: Boolean,
			default: null,
		},
		title: {
			type: String,
			default: "提示",
		},
		width: {
			type: String,
			default: "520px",
		},
		okText: {
			type: String,
			default: "确认",
		},
		cancelText: {
			type: String,
			default: "取消",
		},
		closable: {
			type: Boolean,
			default: true,
		},
		maskClosable: {
			type: Boolean,
			default: true,
		},
		keyboard: {
			type: Boolean,
			default: true,
		},
		confirmLoading: {
			type: Boolean,
			default: false,
		},
		destroyOnClose: {
			type: Boolean,
			default: false,
		},
		showFooter: {
			type: Boolean,
			default: true,
		},
	},
	computed: {
		isVisible() {
			return this.visible === null ? this.value : this.visible;
		},
	},
	mounted() {
		document.addEventListener("keydown", this.handleKeydown);
	},
	beforeDestroy() {
		document.removeEventListener("keydown", this.handleKeydown);
	},
	methods: {
		// 通知父组件弹窗状态变化
		emitVisible(next) {
			this.$emit("input", next);
			this.$emit("update:visible", next);
			this.$emit("visible-change", next);
		},
		// 处理键盘事件：esc退出弹窗
		handleKeydown(e) {
			if (!this.keyboard || !this.isVisible) return;
			if (e.key === "Escape") {
				this.handleCancel(e);
			}
		},
		// 处理遮罩层点击事件：点击遮罩层退出弹窗
		onMaskClick(e) {
			if (!this.maskClosable) return;
			this.handleCancel(e);
		},
		// 处理取消按钮点击事件
		handleCancel(e) {
			this.$emit("cancel", e);
			this.emitVisible(false);
		},
		// 处理确认按钮点击事件
		handleOk(e) {
			this.$emit("ok", e);
		},
	},
};
</script>

<style scoped>
.modal-mask {
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	z-index: 1000;
	background: rgba(0, 0, 0, 0.45);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
}

.modal-wrap {
	max-width: 96vw;
	max-height: 92vh;
	overflow-y: auto;
	background: var(--color-bg-surface);
	color: var(--text-color-primary);
	border: 1px solid var(--color-border-primary);
	border-radius: 8px;
	box-shadow: 0 14px 30px rgba(0, 0, 0, 0.18);
}

.modal-header {
	height: 56px;
	padding: 0 20px;
	border-bottom: 1px solid var(--color-border-primary);
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.modal-title {
	font-size: 16px;
	color: var(--text-color-primary);
	font-weight: 500;
}

.modal-close {
	border: none;
	background: transparent;
	color: var(--text-color-secondary);
	font-size: 16px;
	width: 28px;
	height: 28px;
	border-radius: 4px;
	cursor: pointer;
}

.modal-close:hover {
	color: var(--interactive-text-active);
	background: var(--color-bg-muted);
}

.modal-body {
	padding: 20px;
	color: var(--text-color-primary);
}

.modal-footer {
	border-top: 1px solid var(--color-border-primary);
	padding: 10px 16px;
	display: flex;
	justify-content: flex-end;
	gap: 8px;
}

.modal-btn {
	border: 1px solid var(--color-border-primary);
	background: var(--color-bg-muted);
	color: var(--text-color-primary);
	border-radius: 6px;
	height: 32px;
	line-height: 30px;
	padding: 0 14px;
	cursor: pointer;
}

.modal-btn:hover {
	border-color: var(--interactive-text-active);
	background: var(--color-bg-surface);
	color: var(--interactive-text-active);
}

.modal-btn:disabled {
	cursor: not-allowed;
	opacity: 0.6;
}

.modal-btn-primary {
	border-color: var(--color-button-primary-bg);
	background: var(--color-button-primary-bg);
	color: var(--color-button-primary-text);
}

.modal-btn-primary:hover {
	border-color: var(--color-button-primary-hover-bg);
	background: var(--color-button-primary-hover-bg);
	color: var(--color-button-primary-text);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
	transition: opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
	opacity: 0;
}

@media (max-width: 768px) {
	.modal-wrap {
		width: 100% !important;
	}

	.modal-header {
		padding: 0 14px;
	}

	.modal-body {
		padding: 14px;
	}

	.modal-footer {
		padding: 10px 14px;
	}
}
</style>
